import styled, { css } from 'styled-components'
import { zLayers } from 'utils/styling/zLayers'

export const Backdrop = styled.div.attrs({
  'data-editor-text-modal-backdrop': '',
})`
  position: fixed;
  inset: 0;
  z-index: ${zLayers.itemControlOverlay + 1};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 10, 0.45);
`

export const Dialog = styled.div<{ $width?: string }>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width ?? '1000px'};
  max-width: 92vw;
  max-height: 86vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 8px;
  box-shadow:
    0 2px 20px rgba(0, 0, 0, 0.1),
    0 16px 48px rgba(0, 0, 0, 0.18);
`

export const Header = styled.div`
  position: relative;
  padding: 16px 24px 0;
`

export const Title = styled.div`
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
`

export const Subtitle = styled.div`
  margin-top: 4px;
  color: #737373;
  font-size: 12.5px;
`

export const CloseBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`

export const Section = styled.div`
  padding: 0 24px;
`

export const Prose = styled.p`
  margin: 12px 0 4px;
  color: #404040;
  font-size: 13px;
  line-height: 1.5;
`

export const SectionLabel = styled.div`
  margin: 12px 0 6px;
  color: #737373;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

type PreviewCardProps = {
  $block?: boolean
}

export const PreviewCard = styled.div<PreviewCardProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  padding: 14px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #ffffff;
  overflow: hidden;
  ${({ $block }) =>
    $block &&
    css`
      align-items: flex-start;
      justify-content: flex-start;
      max-height: 220px;
      overflow-y: auto;
    `}
`

type PreviewTextProps = {
  $block?: boolean
}

export const PreviewText = styled.span<PreviewTextProps>`
  max-width: 100%;
  text-align: center;
  line-height: 1.3;
  overflow-wrap: break-word;
  ${({ $block }) =>
    $block &&
    css`
      width: 100%;
      text-align: left;
      white-space: pre-wrap;
    `}
`

export const PreviewGhost = styled.span`
  color: #a3a3a3;
  font-size: 14px;
`

export const StyleBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

export const MenuWrap = styled.div.attrs({ 'data-editor-text-sb-menu': '' })`
  position: relative;
  display: inline-flex;
`

export const PartList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

type DragRowProps = {
  $dragging?: boolean
  $dropSide?: 'before' | 'after' | null
}

/**
 * A draggable row's drag state: `$dropSide` paints the drop line on the edge
 * a dragged row will land on; `$dragging` fades the row in flight.
 */
const dragRowState = ({ $dragging, $dropSide }: DragRowProps) => `
  opacity: ${$dragging ? 0.4 : 1};
  ${
    $dropSide
      ? `
    &::after {
      content: '';
      position: absolute;
      left: 6px;
      right: 6px;
      ${$dropSide === 'before' ? 'top: -3px;' : 'bottom: -3px;'}
      height: 2px;
      border-radius: 1px;
      background: #2a8acd;
      pointer-events: none;
    }
  `
      : ''
  }
`

export const PartRow = styled.div<DragRowProps & { $hidden?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: ${({ $hidden }) => ($hidden ? '#fafafa' : '#ffffff')};
  ${dragRowState}
`

export const PartLabel = styled.span<{ $hidden?: boolean }>`
  flex: none;
  min-width: 96px;
  color: ${({ $hidden }) => ($hidden ? '#a3a3a3' : '#0a0a0a')};
  font-size: 13px;
  text-decoration: ${({ $hidden }) => ($hidden ? 'line-through' : 'none')};
`

export const PartToggle = styled.input`
  flex: none;
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: #2a8acd;
  cursor: pointer;
`

/** The grip a part is dragged by; focusable, so arrow keys can move it too. */
export const PartDragHandle = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  color: #737373;
  cursor: grab;
  user-select: none;
  touch-action: none;
  &:hover {
    background: #f5f5f5;
  }
  &:active {
    cursor: grabbing;
  }
  &:focus-visible {
    outline: 2px solid #2a8acd;
    outline-offset: 1px;
  }
`

export const PartResetBtn = styled.button`
  align-self: flex-start;
  margin-top: 8px;
  padding: 4px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #ffffff;
  color: #525252;
  font-size: 12px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

export const PartValueInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #ffffff;
  color: #262626;
  font-size: 12.5px;
  &:focus {
    outline: none;
    border-color: #2a8acd;
  }
  &::placeholder {
    color: #a3a3a3;
  }
`

/** The rows of a case-detail table under edit, one card each. */
export const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RowCard = styled.div<DragRowProps>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #ffffff;
  ${dragRowState}
`

/** A row card's grip, centred on the card's inputs. */
export const RowDragHandle = styled(PartDragHandle)`
  align-self: center;
`

/** A row's inputs, wrapping into columns when the row has many. */
export const RowFields = styled.div`
  display: grid;
  flex: 1;
  min-width: 0;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 4px 10px;
`

export const RowCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: end;
  margin-bottom: 10px;
  color: #262626;
  font-size: 12.5px;
`

export const RowRemoveBtn = styled.button`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: 18px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`

export const AddRowBtn = styled.button`
  align-self: flex-start;
  margin-top: 8px;
  padding: 6px 12px;
  border: 1px solid #2a8acd;
  border-radius: 4px;
  background: #ffffff;
  color: #2a8acd;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #eef6fc;
  }
`

export const CompositePart = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
`

export const CompositePartLabel = styled.span`
  color: #737373;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 14px 24px 16px;
  border-top: 1px solid #f0f0f0;
`

export const FooterSpacer = styled.div`
  flex: 1;
`

export const PrimaryBtn = styled.button`
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  background: #2a8acd;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #1b6cab;
  }
`

export const GhostBtn = styled.button`
  height: 32px;
  padding: 0 14px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #ffffff;
  color: #0a0a0a;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`

/** The footer's second commit: the primary's blue as an outline. */
export const SecondaryBtn = styled.button`
  height: 32px;
  padding: 0 14px;
  border: 1px solid #2a8acd;
  border-radius: 4px;
  background: #ffffff;
  color: #2a8acd;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #eef6fc;
  }
  &:disabled {
    border-color: #e5e5e5;
    color: #a3a3a3;
    background: #ffffff;
    cursor: default;
  }
`

export const DangerBtn = styled.button`
  height: 32px;
  padding: 0 14px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #ffffff;
  color: #737373;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    border-color: #fca5a5;
    background: #fef2f2;
    color: #b91c1c;
  }
`

export const ValueInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #ffffff;
  color: #0a0a0a;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #2a8acd;
  }
  &::placeholder {
    color: #9a9a9a;
  }
`

export const ValueTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 160px;
  max-height: 320px;
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #ffffff;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1.5;
  font-family: inherit;
  resize: vertical;
  overflow-y: auto;
  &:focus {
    outline: none;
    border-color: #2a8acd;
  }
  &::placeholder {
    color: #9a9a9a;
  }
`

export const ReadOnlyValue = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #f5f5f5;
  color: #525252;
  font-size: 14px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`

export const Hint = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 11px;
  line-height: 1.4;
`

export const ErrorText = styled.div`
  margin-top: 6px;
  color: #cc0000;
  font-size: 11px;
  line-height: 1.4;
`

export const SplitBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 24px;
  padding: 4px 24px 8px;
`

export const SplitValueCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 300px;
  max-width: 100%;
`

export const SplitSideCol = styled.div`
  display: flex;
  flex: 1 1 320px;
  flex-direction: column;
  min-width: 260px;
`
